import { DesignChannel } from 'components/NewFileModal/Types'
import { StartWithMedia } from '../../components/StartWithMedia/StartWithMedia'
import { PropsWithChildren, useMemo, useState } from 'react'
import { IMediaFile } from 'components/Design/UploadsTab/Types'
import { SetupBasics } from '../../components/SetupBasics/SetupBasics'
import { MultiStepMobileWizard } from 'components/Wizards/MobileWizards/MultiStepMobileWizard/MultiStepMobileWizard'
import { observer } from 'mobx-react-lite'
import { useMobileWizardCreateDesign } from 'components/Wizards/MobileWizards/hooks/useMobileWizardCreateDesign'
import { PickBackground } from '../../components/PickBackground/PickBackground'
import { ViewYourDesign } from '../../components/ViewYourDesign/ViewYourDesign'
import { AlmanacFileSummary } from 'types'
import { DesignValues, FlowProps } from 'components/Wizards/MobileWizards/Types'
import { SubmitStepProps } from 'components/Wizards/MobileWizards/MultiStepMobileWizard/Types'

type Values = Pick<
  DesignValues,
  | 'content'
  | 'initial_media_file_id'
  | 'initial_media_file_remove_background'
  | 'width'
  | 'height'
  | 'filename'
  | 'project'
  | 'source_visual_element_id'
>

interface Props {
  onSubmitStep: ({ values, currentStep, setCurrentStep, advance }: SubmitStepProps<Values>) => Promise<void>
}

const FlowBWithInitialTemplate = ({ schema, ...props }: FlowProps<DesignChannel>) => {
  const [selectedMedia, setSelectedMedia] = useState<IMediaFile | undefined>()

  const {
    meta: { templateBorderRadius, channel },
  } = schema

  const schemaWithoutPickBackgroundStep = useMemo(() => {
    return {
      ...schema,
      steps: [...schema.steps.slice(0, 2), schema.steps[3]],
    }
  }, [schema])

  const [design, setDesign] = useState<AlmanacFileSummary | undefined>()
  const { createDesign } = useMobileWizardCreateDesign(channel, {
    navigateToDesignOnComplete: false,
    loadingToast: 'Generating...',
  })

  const handleSubmitStep = async ({ values, currentStep, setCurrentStep, advance }: SubmitStepProps<Values>) => {
    const finish = async () => {
      const design = await createDesign(values)

      if (design) {
        setDesign(design)
        setCurrentStep(2)
      }
    }

    if (currentStep === 1) {
      await finish()
    } else {
      advance()
    }
  }

  return (
    <FlowB schema={schemaWithoutPickBackgroundStep} {...props} onSubmitStep={handleSubmitStep}>
      <StartWithMedia onSelect={setSelectedMedia} />
      {selectedMedia && (
        <SetupBasics
          selectedMedia={selectedMedia}
          initialValues={{
            content: undefined,
            source_visual_element_id: props.initialValues?.source_visual_element_id,
          }}
        />
      )}
      <ViewYourDesign design={design} selectedMedia={selectedMedia} borderRadius={templateBorderRadius} />
    </FlowB>
  )
}

const FlowBWithoutInitialTemplate = ({ schema, ...props }: FlowProps<DesignChannel>) => {
  const [selectedMedia, setSelectedMedia] = useState<IMediaFile | undefined>()

  const {
    meta: { templateBorderRadius, channel },
  } = schema

  const [design, setDesign] = useState<AlmanacFileSummary | undefined>()
  const { createDesign } = useMobileWizardCreateDesign(channel, {
    navigateToDesignOnComplete: false,
    loadingToast: 'Generating...',
  })

  const handleSubmitStep = async ({ values, currentStep, setCurrentStep, advance }: SubmitStepProps<Values>) => {
    const { initial_media_file_remove_background } = values

    const finish = async () => {
      const design = await createDesign(values)

      if (design) {
        setDesign(design)
        setCurrentStep(3)
      }
    }

    if (currentStep === 1 && !initial_media_file_remove_background) {
      await finish()
    } else if (currentStep === 2) {
      await finish()
    } else {
      advance()
    }
  }

  return (
    <FlowB schema={schema} {...props} onSubmitStep={handleSubmitStep}>
      <StartWithMedia onSelect={setSelectedMedia} />
      {selectedMedia && (
        <SetupBasics
          selectedMedia={selectedMedia}
          initialValues={{ content: undefined, source_visual_element_id: undefined }}
        />
      )}
      <PickBackground
        templateBorderRadius={templateBorderRadius}
        initialValues={{ content: undefined, source_visual_element_id: undefined }}
      />
      <ViewYourDesign design={design} selectedMedia={selectedMedia} borderRadius={templateBorderRadius} />
    </FlowB>
  )
}

const FlowB = observer(
  ({
    schema,
    onClose,
    initialValues,
    children,
    onSubmitStep,
    ...props
  }: PropsWithChildren<FlowProps<DesignChannel>> & Props) => (
    <MultiStepMobileWizard<Values>
      schema={schema}
      onClose={onClose}
      onSubmitStep={onSubmitStep}
      initialValues={{ filename: schema.meta.title, ...initialValues }}
      {...props}
    >
      {children}
    </MultiStepMobileWizard>
  ),
)

const FlowBContainer = ({ initialValues, ...props }: FlowProps<DesignChannel>) => {
  if (initialValues?.source_visual_element_id) {
    return <FlowBWithInitialTemplate initialValues={initialValues} {...props} />
  }

  return <FlowBWithoutInitialTemplate initialValues={initialValues} {...props} />
}

export { FlowBContainer as FlowB }
